import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Truck, Star, AlertTriangle, CalendarDays } from "lucide-react";
import { Card } from "../ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["L", "M", "M", "G", "V", "S", "D"];

export const CARRIER_STYLE = {
  brt: { name: "BRT", pill: "bg-emerald-100 text-emerald-800 border-emerald-300", solid: "bg-emerald-600" },
  stef: { name: "STEF", pill: "bg-sky-100 text-sky-800 border-sky-300", solid: "bg-sky-600" },
};

export const todayIso = () => new Date().toISOString().slice(0, 10);

export const CarrierPill = ({ kind, data, recommended, testid }) => {
  const st = CARRIER_STYLE[kind];
  if (!data?.served) return null;
  return (
    <span className={`inline-flex items-center gap-1 border rounded px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap ${st.pill} ${recommended ? "ring-1 ring-offset-0 ring-current" : ""}`} title={`${st.name} ${data.label} — consegna ${data.delivery?.weekday} ${data.delivery?.date}`} data-testid={testid}>
      {recommended && <Star className="w-2.5 h-2.5 fill-current" />}{st.name} {data.label} → {data.delivery?.short}
    </span>
  );
};

/** Badge compatti per liste (dati precalcolati dagli endpoint batch). */
export const DeliveryBadges = ({ info, testid }) => {
  if (!info) return null;
  if (!info.cap) return <span className="text-[10px] text-stone-400 italic" data-testid={testid ? `${testid}-nocap` : undefined}>CAP mancante</span>;
  const none = !info.brt?.served && !info.stef?.served;
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={testid}>
      <CarrierPill kind="brt" data={info.brt} recommended={info.recommended === "brt"} testid={testid ? `${testid}-brt` : undefined} />
      <CarrierPill kind="stef" data={info.stef} recommended={info.recommended === "stef"} testid={testid ? `${testid}-stef` : undefined} />
      {none && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5" data-testid={testid ? `${testid}-none` : undefined}><AlertTriangle className="w-2.5 h-2.5" /> Nessun corriere per CAP {info.cap}</span>}
      {info.basis === "today" && !none && <span className="text-[9px] text-stone-400">se spedito {info.ship?.short}</span>}
    </div>
  );
};

/** Riepilogo per molti CAP (liste clienti): ritorna mappa cap → {brt, stef, recommended}. */
export const useCapsSummary = (caps) => {
  const [map, setMap] = useState({});
  const key = useMemo(() => Array.from(new Set((caps || []).filter(Boolean).map((c) => String(c).replace(/\D/g, "").padStart(5, "0")))).sort().join(","), [caps]);
  useEffect(() => {
    if (!key) { setMap({}); return; }
    axios.post(`${API}/logistics/caps-summary`, { caps: key.split(",") }).then((r) => setMap(r.data || {})).catch(() => {});
  }, [key]);
  return (cap) => {
    const k = String(cap || "").replace(/\D/g, "").padStart(5, "0");
    const v = map[k];
    return v ? { ...v, cap: k } : (cap ? undefined : { cap: null });
  };
};

const PlanDays = ({ days, handover }) => (
  <span className="inline-flex gap-0.5" title={handover ? "Giorni di affidamento da Prato" : "Giorni di passaggio in località"}>
    {DAYS.map((d, i) => (
      <span key={i} className={`w-4 h-4 rounded text-[9px] font-bold flex items-center justify-center ${days?.[i] ? "bg-sky-600 text-white" : "bg-stone-100 text-stone-300"}`}>{d}</span>
    ))}
  </span>
);

const CarrierBlock = ({ kind, data, recommended, cap }) => {
  const st = CARRIER_STYLE[kind];
  if (!data?.served) {
    return (
      <div className="rounded-lg border border-dashed border-stone-300 p-3 text-xs text-stone-500" data-testid={`delivery-${kind}-none`}>
        <p className="font-bold text-stone-600">{st.name}</p>
        <p>{data?.expired ? "Piano di trasporto scaduto per questa località" : data?.reason || `Non serve questo CAP`}</p>
      </div>
    );
  }
  return (
    <div className={`rounded-lg border p-3 text-xs ${recommended ? "border-2 " + (kind === "brt" ? "border-emerald-500 bg-emerald-50/60" : "border-sky-500 bg-sky-50/60") : "border-stone-200 bg-white"}`} data-testid={`delivery-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-extrabold text-sm flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${st.solid}`} /> {st.name} <span className="font-mono text-stone-700">{data.label}</span></p>
        {recommended && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5"><Star className="w-3 h-3 fill-current" /> Consigliato</span>}
      </div>
      <p className="mt-1.5 text-stone-800">Consegna <strong className="text-base" data-testid={`delivery-${kind}-date`}>{data.delivery?.weekday} {data.delivery?.date?.slice(8, 10)}/{data.delivery?.date?.slice(5, 7)}</strong></p>
      <p className="text-stone-500">Affidamento {data.handover?.short}{data.base_label && data.base_label !== data.label ? ` · tempi di resa ${data.base_label} → effettivo ${data.label}` : ""}</p>
      {kind === "stef" && (
        <div className="mt-1.5 space-y-1 text-stone-600">
          <p>Filiale: <strong>{data.filiale || "—"}</strong>{data.filiale_resa && data.filiale_resa !== data.filiale ? ` (resa ${data.filiale_resa})` : ""} · Prov. {data.provincia}</p>
          <p className="flex items-center gap-1.5">Passaggio: <PlanDays days={data.plan_days} /></p>
          <p className="flex items-center gap-1.5">Affidamento da Prato: <PlanDays days={[0, 1, 2, 3, 4, 5, 6].map((i) => (data.handover_days || []).includes(i))} handover /></p>
          {data.localita && <p>Località piano: {data.localita}{data.plan_cap && data.plan_cap !== cap ? ` (CAP generico ${data.plan_cap})` : ""}</p>}
        </div>
      )}
      {(data.notes || []).map((n, i) => <p key={i} className="mt-1 text-amber-800">• {n}</p>)}
      {(data.warnings || []).map((n, i) => <p key={`w${i}`} className="mt-1 text-red-700 font-semibold">⚠ {n}</p>)}
      {data.localita_match === false && <p className="mt-1 text-amber-800">• Località non trovata nel cappario: verificare che il CAP sia corretto</p>}
    </div>
  );
};

/** Scheda completa: chi consegna, quando, con simulazione sulle prossime date di spedizione. */
export const DeliveryCard = ({ cap, localita, shipDate, simulate = 7, onPickDate, compact = false, className = "", testid = "delivery-card", orderId }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [noCap, setNoCap] = useState(false);
  const [locOverride, setLocOverride] = useState("");
  const capN = String(cap || "").replace(/\D/g, "");
  const canQuery = capN.length === 5 || !!orderId;
  const loc = locOverride || localita || "";
  useEffect(() => { setLocOverride(""); }, [cap, orderId]);
  useEffect(() => {
    if (!canQuery) { setData(null); return; }
    let cancelled = false;
    axios.get(`${API}/logistics/lookup`, { params: { cap: capN.length === 5 ? capN : undefined, order_id: capN.length === 5 ? undefined : orderId, localita: loc || undefined, ship_date: shipDate || todayIso(), simulate } })
      .then((r) => { if (!cancelled) { setData(r.data); setErr(""); setNoCap(false); } })
      .catch((e) => {
        if (cancelled) return;
        setData(null);
        const msg = e?.response?.data?.detail || "Errore logistica";
        if (/CAP/i.test(msg) && e?.response?.status === 400) { setNoCap(true); setErr(""); } else { setErr(msg); }
      });
    return () => { cancelled = true; };
  }, [capN, orderId, canQuery, loc, shipDate, simulate]);

  if (!canQuery || noCap) {
    return <p className="text-xs text-stone-500 italic flex items-center gap-1" data-testid={`${testid}-nocap`}><Truck className="w-3.5 h-3.5" /> {noCap ? "CAP di consegna mancante: inserisci il CAP in anagrafica cliente per vedere corriere e data di consegna." : "Inserisci il CAP di consegna per vedere corriere e data di consegna."}</p>;
  }
  if (err) return <p className="text-xs text-red-600" data-testid={`${testid}-error`}>{err}</p>;
  if (!data) return <p className="text-xs text-stone-400" data-testid={`${testid}-loading`}>Calcolo consegna…</p>;

  const none = !data.brt?.served && !data.stef?.served;
  return (
    <Card className={`p-3 space-y-3 border-stone-200 ${className}`} data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold text-stone-900 flex items-center gap-2"><Truck className="w-4 h-4 text-[#8A1C2A]" /> Consegna per CAP <span className="font-mono">{data.cap}</span>{data.provincia && <span className="text-stone-500 font-normal">({data.provincia})</span>}</p>
        <p className="text-xs text-stone-500 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Spedizione {data.ship?.short}</p>
      </div>
      {data.localities?.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-stone-500">Località:</span>
          <select value={loc} onChange={(e) => setLocOverride(e.target.value)} className="h-7 border border-stone-300 rounded bg-white px-1 text-xs max-w-[260px]" data-testid={`${testid}-localita-select`}>
            <option value="">{(localita || data.localita) ? `${localita || data.localita} (dall'indirizzo)` : "— scegli la località —"}</option>
            {data.localities.map((l) => <option key={l.name} value={l.name}>{l.name}{l.brt ? " · BRT" : ""}{l.stef ? " · STEF" : ""}</option>)}
          </select>
        </div>
      )}
      {none ? (
        <p className="text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded p-2 flex items-center gap-2" data-testid={`${testid}-none`}><AlertTriangle className="w-4 h-4" /> Nessuno dei due corrieri serve il CAP {data.cap}: verificare il CAP o contattare la logistica.</p>
      ) : (
        <div className={`grid gap-2 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 md:grid-cols-2"}`}>
          <CarrierBlock kind="brt" data={data.brt} cap={data.cap} recommended={data.recommended === "brt"} />
          <CarrierBlock kind="stef" data={data.stef} cap={data.cap} recommended={data.recommended === "stef"} />
        </div>
      )}
      {!none && simulate > 0 && data.simulation?.length > 0 && (
        <div data-testid={`${testid}-simulation`}>
          <p className="text-[11px] font-semibold text-stone-600 mb-1">Simulazione: se spedisco il… {onPickDate && <span className="font-normal text-stone-400">(clicca una riga per impostare la data di spedizione)</span>}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-stone-500 border-b border-stone-200"><th className="py-1 pr-2">Spedizione</th><th className="py-1 pr-2">BRT</th><th className="py-1 pr-2">STEF</th></tr></thead>
              <tbody>
                {data.simulation.map((row) => {
                  const active = row.ship.date === (shipDate || todayIso());
                  return (
                    <tr key={row.ship.date} onClick={() => onPickDate?.(row.ship.date)} className={`border-b border-stone-100 ${onPickDate ? "cursor-pointer hover:bg-stone-50" : ""} ${active ? "bg-amber-50 font-semibold" : ""}`} data-testid={`${testid}-sim-${row.ship.date}`}>
                      <td className="py-1 pr-2 whitespace-nowrap">{row.ship.short}{active && <span className="ml-1 text-[9px] text-amber-700">◀</span>}</td>
                      <td className="py-1 pr-2">{row.brt?.served ? <span className={row.recommended === "brt" ? "text-emerald-800 font-bold" : "text-stone-700"}>{row.brt.label} → {row.brt.delivery.short}</span> : <span className="text-stone-300">—</span>}</td>
                      <td className="py-1 pr-2">{row.stef?.served ? <span className={row.recommended === "stef" ? "text-sky-800 font-bold" : "text-stone-700"}>{row.stef.label} → {row.stef.delivery.short}</span> : <span className="text-stone-300">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
};
